import { MenuSplitButton, ToggleButton, ToggleButtonGroup } from '@atoms/buttons';
import { Box } from '@atoms/layout';
import { Skeleton, Stack, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { AgenciesListPage, FacilitiesListPage, RequestsListPage } from './subpages';
import { useAgencyEditStore } from '@libs/store/agencies/agencyEditDrawerStore';
import { EditAgencyDrawer } from './drawers/editAgency';
import { useFacilityEditStore } from '@libs/store/facilities/editDrawerStore';
import { EditFacilityDrawer } from './drawers/editFacility';
import { useFacilitiesListStore } from '@libs/store/facilities';
import { useEffect } from 'react';
import { useAgenciesListStore } from '@libs/store/agencies';
import { MAX_NUMBER_OF_DOMAINS } from '@libs/config';

type Props = {
  activeTab: 'requests' | 'agencies' | 'facilities';
};
export function ClientsPageLayout({ activeTab }: Props) {
  const navigate = useNavigate();
  const intl = useIntl();
  const { drawerOpen: agencyEditDrawerOpen, openDrawer: openEditAgencyDrawer } = useAgencyEditStore();
  const { drawerOpen: facilityEditDrawerOpen, openDrawer: openEditFacilityDrawer } = useFacilityEditStore();
  const { facilities, loadFacilities, loadingList: facilitiesLoading } = useFacilitiesListStore();
  const { agencies, loadAgencies, loadingList: agenciesLoading } = useAgenciesListStore();

  const loading = facilitiesLoading || agenciesLoading;
  const localizationSubset = 'clients';

  useEffect(() => {
    loadFacilities([], 1, 50);
  }, [loadFacilities]);

  useEffect(() => {
    loadAgencies([], 1, 50);
  }, [loadAgencies]);

  const handleNewAction = () => {
    if (activeTab === 'agencies') {
      handleNewAgency();
    } else {
      handleNewFacility();
    }
  };
  const handleNewFacility = () => {
    openEditFacilityDrawer();
  };
  const handleNewAgency = () => {
    openEditAgencyDrawer();
  };

  return (
    <>
      <Stack
        aria-label="ClientsPageLayoutWrapper"
        spacing={2}
        direction={'column'}
        sx={{ height: '100%', width: '100%' }}
      >
        <Stack direction={'row'} spacing={1} aria-label="ClientsPageHeader">
          <ToggleButtonGroup aria-label="ClientsSubPageSwitcher" value={activeTab}>
            <ToggleButton value="requests" size="large" onClick={() => navigate('/clients/requests')}>
              <FormattedMessage id={`${localizationSubset}.subpageMenu.requests`} />
            </ToggleButton>
            <ToggleButton value="agencies" size="large" onClick={() => navigate('/clients/agencies')}>
              <FormattedMessage id={`${localizationSubset}.subpageMenu.agencies`} />
            </ToggleButton>
            <ToggleButton value="facilities" size="large" onClick={() => navigate('/clients/facilities')}>
              <FormattedMessage id={`${localizationSubset}.subpageMenu.facilities`} />
            </ToggleButton>
          </ToggleButtonGroup>
          <Stack direction="column">
            <Box flex={1} />
            <Stack direction="row" spacing={1}>
              <Typography variant="h6">Domains used:</Typography>
              {loading && <Skeleton width={70} />}
              {!loading && (
                <Typography variant="h6">
                  {facilities.length + agencies.length} of {MAX_NUMBER_OF_DOMAINS}
                </Typography>
              )}
            </Stack>
            <Box flex={1} />
          </Stack>
          <Box flex={1} />
          <MenuSplitButton
            disabled={loading}
            onMainClick={handleNewAction}
            label={intl.formatMessage({
              id: `${localizationSubset}.menuButton.${activeTab === 'agencies' ? 'agency' : 'facility'}`,
            })}
            menuItems={[
              {
                label: intl.formatMessage({ id: `${localizationSubset}.menuButton.agency` }),
                onAction: handleNewAgency,
              },
              {
                label: intl.formatMessage({ id: `${localizationSubset}.menuButton.facility` }),
                onAction: handleNewFacility,
              },
            ]}
          />
        </Stack>
        {activeTab === 'requests' && <RequestsListPage localizationSubset={`${localizationSubset}.requests`} />}
        {activeTab === 'agencies' && <AgenciesListPage localizationSubset={`${localizationSubset}.agencies`} />}
        {activeTab === 'facilities' && <FacilitiesListPage localizationSubset={`${localizationSubset}.facilities`} />}
      </Stack>
      {agencyEditDrawerOpen && (
        <EditAgencyDrawer onSaveCall={() => {}} localizationSubset={`${localizationSubset}.drawers.agency`} />
      )}
      {facilityEditDrawerOpen && (
        <EditFacilityDrawer onSaveCall={() => {}} localizationSubset={`${localizationSubset}.drawers.facility`} />
      )}
    </>
  );
}
